import React, { useState } from 'react'
import {
  Box,
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import PeopleIcon from '@mui/icons-material/People'
import PersonAddIcon from '@mui/icons-material/PersonAdd'

type GenericTransferElementsProps<T> = {
  availableItems: T[]
  items: T[]
  getKey: (item: T) => number
  getLabel: (item: T) => string
  leftTitle: string
  rightTitle: string
  onSave?: (keys: number[]) => void
  buttonTittle?: string
  onRightItemsChange?: (rightItems: T[]) => void
}

const GenericTransferElements = <T,>({
  availableItems,
  items,
  getKey,
  getLabel,
  leftTitle,
  rightTitle,
  onSave,
  buttonTittle,
  onRightItemsChange
}: GenericTransferElementsProps<T>) => {
  const [rightItems, setRightItems] = useState<T[]>(items)
  const [leftItems, setLeftItems] = useState<T[]>(availableItems)
  const [selectedLeft, setSelectedLeft] = useState<Set<number>>(new Set())
  const [selectedRight, setSelectedRight] = useState<Set<number>>(new Set())
  const [searchTerm, setSearchTerm] = useState<string>('')

  const handleToggle = (
    id: number,
    setSelected: React.Dispatch<React.SetStateAction<Set<number>>>
  ) => {
    setSelected((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleMoveToRight = () => {
    const itemsToMove = leftItems.filter((item) =>
      selectedLeft.has(getKey(item))
    )
    if (onRightItemsChange) {
      setRightItems((prev) => {
        const updated = [...prev, ...itemsToMove]
        onRightItemsChange?.(updated)
        return updated
      })
    } else {
      setRightItems((prev) => [...prev, ...itemsToMove])
    }
    setLeftItems((prev) =>
      prev.filter((item) => !selectedLeft.has(getKey(item)))
    )
    setSelectedLeft(new Set())
  }

  const handleMoveToLeft = () => {
    const itemsToMove = rightItems.filter((item) =>
      selectedRight.has(getKey(item))
    )
    setLeftItems((prev) => [...prev, ...itemsToMove])
    if (onRightItemsChange) {
      setRightItems((prev) => {
        const updated = prev.filter((item) => !selectedRight.has(getKey(item)))
        onRightItemsChange?.(updated)
        return updated
      })
    } else {
      setRightItems((prev) =>
        prev.filter((item) => !selectedRight.has(getKey(item)))
      )
    }
    setSelectedRight(new Set())
  }

  const handleSave = () => {
    const keys = rightItems.map(getKey)
    if (onSave) onSave(keys)
  }

  const filteredLeftItems = leftItems.filter((item) =>
    getLabel(item).toLowerCase().includes(searchTerm.toLowerCase())
  )

  const renderList = (
    itemList: T[],
    selected: Set<number>,
    toggle: (id: number) => void,
    emptyText: string
  ) => {
    if (itemList.length === 0) {
      return (
        <Typography variant='body2' color='textSecondary' align='center' style={{ padding: '16px 0' }}>
          {emptyText}
        </Typography>
      )
    }
    return (
      <List dense disablePadding>
        {itemList.map((item) => {
          const key = getKey(item)
          const isSelected = selected.has(key)
          return (
            <ListItem
              key={key}
              onClick={() => toggle(key)}
              style={{
                borderRadius: 6,
                marginBottom: 2,
                cursor: 'pointer',
                backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                borderLeft: isSelected ? '3px solid #1976d2' : '3px solid transparent',
                transition: 'all 150ms ease'
              }}
            >
              <ListItemText
                primary={getLabel(item)}
                primaryTypographyProps={{
                  variant: 'body2',
                  style: { fontWeight: isSelected ? 600 : 400, color: isSelected ? '#1976d2' : '#333' }
                }}
              />
            </ListItem>
          )
        })}
      </List>
    )
  }

  return (
    <Grid container spacing={1} alignItems='stretch'>
      {/* Columna izquierda */}
      <Grid item xs>
        <Paper elevation={2} style={{ borderRadius: 10, overflow: 'hidden' }}>
          {/* Header azul */}
          <Box
            display='flex'
            alignItems='center'
            style={{ backgroundColor: '#1976d2', padding: '8px 10px', gap: 6 }}
          >
            <PeopleIcon style={{ color: '#fff', fontSize: 18 }} />
            <Typography variant='subtitle2' style={{ color: '#fff', fontWeight: 600, flex: 1 }}>
              {leftTitle}
            </Typography>
            <Box
              style={{
                backgroundColor: 'rgba(255,255,255,0.25)',
                borderRadius: 12,
                padding: '1px 8px'
              }}
            >
              <Typography variant='caption' style={{ color: '#fff' }}>
                {filteredLeftItems.length}
              </Typography>
            </Box>
          </Box>
          {/* Cuerpo */}
          <Box style={{ padding: '8px' }}>
            <TextField
              variant='outlined'
              placeholder='Buscar...'
              fullWidth
              size='small'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginBottom: 8 }}
            />
            <Box style={{ minHeight: 60, maxHeight: 75, overflowY: 'auto' }}>
              {renderList(filteredLeftItems, selectedLeft, (id) => handleToggle(id, setSelectedLeft), 'No hay usuarios disponibles')}
            </Box>
          </Box>
        </Paper>
      </Grid>

      {/* Botones centrales */}
      <Grid item>
        <Box display='flex' flexDirection='column' justifyContent='center' alignItems='center' style={{ height: '100%', gap: 8 }}>
          <Tooltip title='Agregar seleccionados'>
            <span>
              <IconButton
                onClick={handleMoveToRight}
                disabled={selectedLeft.size === 0}
                style={{
                  backgroundColor: selectedLeft.size > 0 ? '#1976d2' : '#e0e0e0',
                  color: selectedLeft.size > 0 ? '#fff' : '#aaa',
                  borderRadius: '50%',
                  padding: 8
                }}
              >
                <ArrowForwardIcon fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title='Quitar seleccionados'>
            <span>
              <IconButton
                onClick={handleMoveToLeft}
                disabled={selectedRight.size === 0}
                style={{
                  backgroundColor: selectedRight.size > 0 ? '#e53935' : '#e0e0e0',
                  color: selectedRight.size > 0 ? '#fff' : '#aaa',
                  borderRadius: '50%',
                  padding: 8
                }}
              >
                <ArrowBackIcon fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Grid>

      {/* Columna derecha */}
      <Grid item xs>
        <Paper elevation={2} style={{ borderRadius: 10, overflow: 'hidden' }}>
          {/* Header verde */}
          <Box
            display='flex'
            alignItems='center'
            style={{ backgroundColor: '#2e7d32', padding: '8px 10px', gap: 6 }}
          >
            <PersonAddIcon style={{ color: '#fff', fontSize: 18 }} />
            <Typography variant='subtitle2' style={{ color: '#fff', fontWeight: 600, flex: 1 }}>
              {rightTitle}
            </Typography>
            <Box
              style={{
                backgroundColor: 'rgba(255,255,255,0.25)',
                borderRadius: 12,
                padding: '1px 8px'
              }}
            >
              <Typography variant='caption' style={{ color: '#fff' }}>
                {rightItems.length}
              </Typography>
            </Box>
          </Box>
          {/* Cuerpo */}
          <Box style={{ padding: '8px' }}>
            <Box style={{ minHeight: 120, maxHeight: 150, overflowY: 'auto' }}>
              {renderList(rightItems, selectedRight, (id) => handleToggle(id, setSelectedRight), 'No hay usuarios asignados')}
            </Box>
          </Box>
        </Paper>
      </Grid>

      {/* Botón guardar opcional */}
      {onSave && (
        <Grid item xs={3}>
          <Button
            variant='contained'
            color='primary'
            onClick={handleSave}
            disabled={rightItems.length === 0}
            fullWidth
          >
            {buttonTittle ?? 'Guardar'}
          </Button>
        </Grid>
      )}
    </Grid>
  )
}

export default GenericTransferElements
